import React, { Suspense } from "react";
import { IntlProvider } from "react-intl";
import { I18nextProvider } from "react-i18next";
import flatten from "flat";
import rtlDetect from "rtl-detect";
import produce from "immer";
import { useLDClient } from "launchdarkly-react-client-sdk";
import deterministicStringify from "json-stable-stringify";

import { logger } from "@jslib/shared/utils/logger";
import CustomCssContextProvider from "@jslib/shared/CustomCssContextProvider";

import i18next from "@/lib/intl/i18next";

import { getInstallType } from "@/lib/ServerApi/ApiUrls";
import { useKount } from "@/lib/kount/useKount";
import { useFingerprint } from "@/lib/fingerprint/useFingerprint";

import { getOptionsMessages, modernizeLocale } from "../lib/intl/intl";
import { type ProgramTheme, type VerificationService } from "../lib/types/types";
import { includeIPQSDeviceFingerprintScript } from "../lib/IPQS/IPQSutils";
import { getOptions } from "../options/options";
import { CustomDimensionNames, setUserDimension } from "../lib/GoogleAnalytics/ga";

import { LoadStepComponent as LoadStep } from "./LoadStepComponent";
import { CustomTextWrapper } from "./CustomTextWrapper";
import { TestModeFlagComponent } from "./TestModeFlag/TestModeFlagComponent";
import { LoadingScreenComponent as LoadingScreen } from "./LoadingScreen/LoadingScreenComponent";
import { Layout } from "./Layout/Layout";
import { FormWrapperComponent } from "./FormWrapper/FormWrapperComponent";
import { useFlags } from "./FeatureFlags/hooks";

export const NewVerificationInnerComponent = ({
  locale,
  messages,
  programTheme,
  verificationService,
  programId,
}: {
  // TODO better types:
  locale: any;
  messages: any;
  programTheme: ProgramTheme;
  verificationService: VerificationService;
  programId: string;
}) => {
  const ldClient = useLDClient();
  const options = getOptions();
  const layout = verificationService.programTheme.themeChoices?.landingPage.layout || "center";
  const { renderAs } = options;
  const flags = useFlags();
  useKount(verificationService);
  useFingerprint(verificationService);

  React.useEffect(() => {
    try {
      if (flags && verificationService && verificationService.viewModel) {
        const stringFlags = deterministicStringify(flags);
        setUserDimension(CustomDimensionNames.flags, stringFlags);
        const metadata = verificationService.viewModel.metadata || {};
        const metadataWithFlags = produce(metadata, (draft) => {
          draft.flags = stringFlags;
        });
        const newViewModel = produce(verificationService.viewModel, (draft) => {
          draft.metadata = metadataWithFlags;
        });
        verificationService.updateViewModel(newViewModel);
      }
    } catch (e) {
      logger.error(e);
    }
  }, []);

  React.useEffect(() => {
    includeIPQSDeviceFingerprintScript(
      programTheme,
      verificationService.verificationResponse.verificationId,
    );

    // HC-202: Launch darkly experimentation trial.
    // Keeping this behind a private option so we can turn it on just for hosted programs.
    if (
      verificationService.verificationResponse.verificationId &&
      options._launchDarklyUserTargeting &&
      ldClient
    ) {
      const identity: {
        kind: string;
        user: { key: string; locale?: string; segment?: string; installType?: string };
        program: { key: string };
        account?: { key: string };
      } = {
        kind: "multi",
        user: {
          key: verificationService.verificationResponse.verificationId,
          locale: verificationService.verificationResponse.locale,
          segment: verificationService.verificationResponse.segment,
          installType: getInstallType(),
        },
        program: { key: programId },
      };
      if (programTheme.accountId) {
        identity.account = { key: programTheme.accountId };
      }
      ldClient.identify(identity);
    }
  }, [verificationService.verificationResponse.verificationId, ldClient]);

  React.useEffect(() => {
    try {
      // provides feature flags for mysheerid-preview install script
      getOptions()._launchDarklyFlagCb(flags);
    } catch {
      // Do nothing if LD option is not present
    }
  }, [flags]);

  const messagesWithOptions = { ...messages, ...(flatten(getOptionsMessages(locale)) as object) };

  return (
    // Don't change locale, so we don't have to load pluralization data, which has complications. HD-560
    <IntlProvider
      locale="en"
      key={locale}
      messages={messagesWithOptions}
      textComponent={CustomTextWrapper}
    >
      <CustomCssContextProvider
        themeChoices={programTheme.themeChoices}
        customCssFromOptions={getOptions().customCss}
      >
        <div
          className={`${
            rtlDetect.isRtlLang(modernizeLocale(locale)) ? "sid-rtl" : "sid-ltr"
          } sid-locale-${locale}`}
        >
          <FormWrapperComponent>
            <Layout
              layout={layout}
              verificationService={verificationService}
              renderAsLandingPage={renderAs === "landingPage"}
            >
              <Suspense fallback={<LoadingScreen step={verificationService.loadingStep} />}>
                <I18nextProvider i18n={i18next}>
                  {verificationService.isLoading ? (
                    <LoadingScreen step={verificationService.loadingStep} />
                  ) : (
                    <div>
                      {!getOptions().hideTestModeFlag && (
                        <TestModeFlagComponent verificationService={verificationService} />
                      )}

                      <LoadStep
                        stepToLoad={
                          verificationService.overrideStep ||
                          verificationService.verificationResponse.currentStep
                        }
                        verificationService={verificationService}
                      />
                    </div>
                  )}
                </I18nextProvider>
              </Suspense>
            </Layout>
          </FormWrapperComponent>
        </div>
      </CustomCssContextProvider>
    </IntlProvider>
  );
};
